import { daysSince, formatDate } from '@/shared/lib/dates';
import { messages } from '@/shared/constants/messages';
import { pluralize } from '@/shared/lib/format';
import type { ProjectWithContext } from '@/shared/types/domain';

/** O bastante da última transição para escrever a linha do cabeçalho. */
export interface LastTransition {
  actor_profile_id: string | null;
  actor_name: string;
  created_at: string;
}

/**
 * A linha de atribuição do cabeçalho (spec-project-review.md decisão 14,
 * §6.4): "Aprovado por Pr. João em 06/03". Deriva da ÚLTIMA transição — a
 * mesma consulta da linha do tempo, sem requisição nova.
 *
 * `switch` exaustivo e sem `default`, mesmo motivo de `timelineFor`: situação
 * nova no enum tem de quebrar o build em vez de sair sem atribuição.
 */
export function attributionFor(
  project: ProjectWithContext,
  lastTransition: LastTransition | null,
): string | null {
  if (!lastTransition) return null;

  const date = formatDate(lastTransition.created_at);
  // decisão 1b / spec §6.4: a auto-aprovação fica visível, não proibida — mas
  // só quando o status é RESULTADO de `decide_project` (approved/rejected/
  // changes_requested). Em `under_review` o ator É o autor quase sempre (é o
  // próprio ministério enviando), e marcar isso ali seria ruído, não
  // transparência de governança — a pergunta que a marca responde é "esta
  // pessoa decidiu sobre o próprio projeto?", não "quem escreveu isto?".
  const isDecisionOutcome =
    project.status === 'approved' ||
    project.status === 'rejected' ||
    project.status === 'changes_requested';
  const isSelf =
    isDecisionOutcome &&
    lastTransition.actor_profile_id !== null &&
    lastTransition.actor_profile_id === project.created_by;
  const name = isSelf
    ? `${lastTransition.actor_name} (${messages.review.attributionSelfMarker})`
    : lastTransition.actor_name;

  switch (project.status) {
    case 'draft':
      // Ninguém decidiu nada ainda — nenhuma linha (§6.4, tabela).
      return null;

    /**
     * O estado de espera usa a frase do ENVIO (ADR-013, decisão 2 / decisão
     * 33): "Enviado por Maria em 06/03 · aguarda 6 dias". O crachá já disse
     * onde o projeto está — "Em análise" —, e repetir isso aqui gastaria a
     * única linha que responde quem colocou o projeto ali e há quanto tempo.
     *
     * A frase "Em análise por Fulano desde…" MORREU junto com a reivindicação:
     * outro revisor não quer saber quem está olhando agora, e o dado que
     * interessa — quem decidiu — continua nos ramos abaixo.
     */
    case 'under_review': {
      const base = messages.review.attributionSubmitted(name, date);
      if (!project.submitted_at) return base;
      const days = daysSince(project.submitted_at);
      return `${base}${messages.review.attributionWaitingSuffix(`aguarda ${pluralize(days, 'dia', 'dias')}`)}`;
    }

    case 'changes_requested':
      return messages.review.attributionChangesRequested(name, date);

    case 'approved':
      return messages.review.attributionApproved(name, date);

    case 'rejected':
      return messages.review.attributionRejected(name, date);

    case 'cancelled':
      return messages.review.attributionCancelled(name, date);

    case 'completed':
      return messages.review.attributionCompleted(name, date);
  }
}
